import React from 'react';
import { Box, Tooltip, Typography, Chip, IconButton, useTheme } from '@mui/material';
import {
  SignalWifi4Bar,
  SignalWifi3Bar,
  SignalWifi2Bar,
  SignalWifi1Bar,
  SignalWifiOff,
  InfoOutlined
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface ConnectionQualityInfo {
  level: 'excellent' | 'good' | 'poor' | 'unknown';
  quality: 'HIGH' | 'MEDIUM' | 'LOW';
  deviceCapabilities?: {
    supports4K: boolean;
    supports1080p: boolean;
    maxWidth: number;
    maxHeight: number;
  };
}

interface ConnectionQualityIndicatorProps {
  connectionInfo: ConnectionQualityInfo;
  compact?: boolean;
}

const ConnectionQualityIndicator: React.FC<ConnectionQualityIndicatorProps> = ({
  connectionInfo,
  compact = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const getQualityIcon = (level: ConnectionQualityInfo['level']) => {
    switch (level) {
      case 'excellent':
        return <SignalWifi4Bar sx={{ color: theme.palette.success.main }} />;
      case 'good':
        return <SignalWifi3Bar sx={{ color: theme.palette.warning.main }} />;
      case 'poor':
        return <SignalWifi2Bar sx={{ color: theme.palette.error.main }} />;
      case 'unknown':
      default:
        return <SignalWifi1Bar sx={{ color: '#9e9e9e' }} />;
    }
  };

  const getQualityColor = (level: ConnectionQualityInfo['level']) => {
    switch (level) {
      case 'excellent':
        return theme.palette.success.main;
      case 'good':
        return theme.palette.warning.main;
      case 'poor':
        return theme.palette.error.main;
      case 'unknown':
      default:
        return '#9e9e9e';
    }
  };

  const getQualityLabel = (level: ConnectionQualityInfo['level']) => {
    switch (level) {
      case 'excellent':
        return t('connection.excellent', 'Excellent');
      case 'good':
        return t('connection.good', 'Good');
      case 'poor':
        return t('connection.poor', 'Poor');
      case 'unknown':
      default:
        return t('connection.unknown', 'Unknown');
    }
  };

  const getStreamingQualityLabel = (quality: ConnectionQualityInfo['quality']) => {
    switch (quality) {
      case 'HIGH':
        return '4K (3840×2160)';
      case 'MEDIUM':
        return '1080p (1920×1080)';
      case 'LOW':
        return '720p (1280×720)';
      default:
        return 'Unknown';
    }
  };

  const getStreamingQualityColor = (quality: ConnectionQualityInfo['quality']) => {
    switch (quality) {
      case 'HIGH':
        return theme.palette.success.main;
      case 'MEDIUM':
        return theme.palette.warning.main;
      case 'LOW':
        return theme.palette.error.main;
      default:
        return '#9e9e9e';
    }
  };

  const tooltipContent = (
    <Box sx={{ p: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        {t('connection.qualityInfo', 'Connection Quality Information')}
      </Typography>

      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {t('connection.networkQuality', 'Network Quality')}:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {getQualityIcon(connectionInfo.level)}
          <Typography variant="body2">
            {getQualityLabel(connectionInfo.level)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {t('connection.streamingQuality', 'Streaming Quality')}:
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {getStreamingQualityLabel(connectionInfo.quality)}
        </Typography>
      </Box>

      {connectionInfo.deviceCapabilities && (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {t('connection.deviceCapabilities', 'Device Capabilities')}:
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {t('connection.maxResolution', 'Max Resolution')}: {connectionInfo.deviceCapabilities.maxWidth}×{connectionInfo.deviceCapabilities.maxHeight}
          </Typography>
          <Typography variant="body2">
            {t('connection.supports4K', 'Supports 4K')}: {connectionInfo.deviceCapabilities.supports4K ? t('common.yes', 'Yes') : t('common.no', 'No')}
          </Typography>
          <Typography variant="body2">
            {t('connection.supports1080p', 'Supports 1080p')}: {connectionInfo.deviceCapabilities.supports1080p ? t('common.yes', 'Yes') : t('common.no', 'No')}
          </Typography>
        </Box>
      )}

      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
        {t('connection.qualityNote', 'Quality automatically adjusts based on network conditions')}
      </Typography>
    </Box>
  );

  if (compact) {
    return (
      <Tooltip title={tooltipContent} arrow placement="bottom">
        <IconButton size="small" sx={{ p: 0.5 }}>
          {getQualityIcon(connectionInfo.level)}
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipContent} arrow placement="bottom">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {getQualityIcon(connectionInfo.level)}
          <Typography variant="body2" sx={{ color: getQualityColor(connectionInfo.level) }}>
            {getQualityLabel(connectionInfo.level)}
          </Typography>
        </Box>

        <Chip
          label={getStreamingQualityLabel(connectionInfo.quality)}
          size="small"
          sx={{
            backgroundColor: getStreamingQualityColor(connectionInfo.quality),
            color: 'white',
            fontSize: '0.75rem',
            height: 20,
            '& .MuiChip-label': {
              px: 1,
            },
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default ConnectionQualityIndicator;
